import { Link } from '@tanstack/react-router'
import { useRooms, useStories, type Story } from '@lb/domain'
import { useTranslation } from '@lb/i18n'
import { IconBook, IconChevronRight, IconCommunity, IconStar4, Skeleton, cn } from '@lb/ui'

import { http } from '../../lib/client'
import { useContentLocale } from '../../lib/content-locale'
import { formatRelativeTime } from '../../lib/format'
import { SectionHead } from './journey'

/**
 * `.stories` — "Stories from the road" (journey.jsx:768).
 *
 * The unedited log: posts you wrote, notes you kept. Reached round 14, when we
 * learned `GET /journey/stories` had existed all along beside the voyage.
 *
 * ── `unavailable` is rendered, not swallowed ──────────────────────────────
 * `milestone` is declared in `kind` and never returned; it comes back inside
 * `unavailable` with a reason instead. That is the backend distinguishing "we
 * cannot compute this yet" from "you have none", and dropping the field would
 * collapse the two back together — a member with three notes and no computable
 * milestones would be shown a list that silently claims to be complete.
 *
 * So the section says which kinds are missing. Not an error banner: nothing has
 * failed, and a red box over somebody's own history would be a lie in the other
 * direction.
 */
export function StoriesBlock() {
  const { t } = useTranslation()
  const stories = useStories(http)

  if (stories.isPending) return <Skeleton className="h-40 w-full" />
  /* Quiet on failure, like the map: this is one section of a page that works
     without it, not the page itself. */
  if (stories.isError || !stories.data) return null

  const { items, unavailable } = stories.data
  const missing = unavailable ?? []

  if (items.length === 0 && missing.length === 0) return null

  return (
    <section className="mb-[72px]">
      <SectionHead
        eyebrow={t('stories.eyebrow')}
        title={t('stories.title')}
        sub={t('stories.sub')}
      />

      {items.length > 0 ? (
        /* `.st-list` — three across, not a stack. These are short cards and the
           design shelves them; stacked, six months of history is a scroll. */
        <div className="grid gap-4 min-[641px]:grid-cols-2 min-[1101px]:grid-cols-3">
          {items.map((story, index) => (
            <StoryCard key={`${story.kind}-${story.day}-${story.at}-${index}`} story={story} />
          ))}
        </div>
      ) : (
        <p className="rounded-[var(--r)] border border-dashed border-border p-card text-[14px] text-muted">
          {t('stories.empty')}
        </p>
      )}

      {/*
        ── `missing` is deliberately NOT rendered ────────────────────────────
        The feed reports story kinds it cannot compute yet, and an earlier
        version printed them — «Not computed yet: Milestone marked.» — under a
        parent's own history. That sentence is a status report about our
        pipeline, and the person reading this page is not its operator: to a
        parent it reads as something of theirs failing to load. An honesty
        line about unbuilt computation belongs on an admin surface; here the
        kinds simply do not appear until they exist.
      */}
    </section>
  )
}

const GLYPH = {
  post: IconCommunity,
  note: IconBook,
  milestone: IconStar4,
} as const

/**
 * `.story-card` — and the kind is carried by the whole card, not a left edge.
 *
 * The design tints a post and a milestone with a diagonal wash out of the top
 * corner (`--sage-light` and `--rose-light`, fading to the card fill by 50%)
 * and colours the kind label to match. We had a 3px coloured border on the
 * inline-start edge instead — legible, but a different idea: an edge marks a
 * category, a wash makes the card feel like the thing it holds.
 *
 * ── The wash is light-theme only, deliberately ───────────────────────────
 * `--sage-light` and `--rose-light` are FIXED tints that stay bright in dark
 * mode, while the card fill they fade into flips to navy. The gradient would
 * therefore run bright → dark under text that is light in dark mode, and the
 * top corner of every post card would lose its label. In dark the card keeps
 * the tinted BORDER, which carries the same distinction without the hazard.
 */
function StoryCard({ story }: { story: Story }) {
  const { t, i18n } = useTranslation()
  const locale = useContentLocale()
  const rooms = useRooms(http, locale)
  const Glyph = GLYPH[story.kind]

  const roomName = story.roomId
    ? rooms.data?.items.find((r) => r.id === story.roomId)?.name
    : undefined

  return (
    <article
      className={cn(
        'flex flex-col gap-3 rounded-[var(--r-lg)] border px-6 py-[22px]',
        'transition-[border-color,transform] duration-150 hover:-translate-y-0.5',
        /* Posts and milestones sit on the amande wash; the rest on white
           (accent policy: no green or rose card washes). */
        story.kind === 'post' || story.kind === 'milestone'
          ? 'border-border bg-cream-dark'
          : 'border-border bg-surface',
      )}
    >
      {/* `.st-meta` — kind on the left, day and age on the right. */}
      <header className="flex flex-wrap items-center justify-between gap-2.5">
        <span
          className={cn(
            'inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.1em]',
            story.kind === 'post' || story.kind === 'milestone' ? 'text-ink' : 'text-ink-soft',
          )}
        >
          <Glyph size={13} title={null} />
          {t(`stories.kind.${story.kind}`)}
        </span>
        {/*
         * `.st-day` — "Day 184 · 1 h ago", in the numeric face.
         *
         * The mock's second half is RELATIVE, and that is the half that does
         * the work: "Aug 14, 2026" is a fact you have to date-arithmetic
         * against today, while "2 hours ago" is the feeling of a log.
         */}
        <span className="font-num text-[11px] font-bold text-muted">
          {t('voyage.day', { day: story.day })} · {formatRelativeTime(story.at, i18n.language)}
        </span>
      </header>

      <p className="text-[14px] leading-[1.55] text-ink-soft">{story.body}</p>

      {/*
       * `.st-foot` — where it came from, and the way back to it.
       *
       * "Reopen" only where there IS something to reopen: a private note has no
       * destination — it was never a page. The design draws the link on every
       * card because its fixture is all posts; rendering a dead one here would
       * be a control that does nothing on the majority of a quiet member's rows.
       */}
      <footer className="mt-2 flex items-center justify-between gap-3 border-t border-border pt-3 text-[11.5px]">
        <span className="min-w-0 truncate text-muted">
          {roomName ?? t(`stories.where.${story.kind}`)}
        </span>
        {story.kind === 'post' && story.roomId ? (
          <Link
            to="/community/$roomId"
            params={{ roomId: story.roomId }}
            className="inline-flex flex-none items-center gap-0.5 font-semibold text-ink transition-colors hover:underline"
          >
            {t('stories.reopen')}
            <IconChevronRight size={12} title={null} />
          </Link>
        ) : null}
      </footer>
    </article>
  )
}
